import React, { memo, useState, useCallback, useMemo, useRef } from 'react';
import { Modal, Tabs, Loader, Icon, createUseStyles } from '@Sage/hoi-poi-ui';
import { useWeb5Context } from 'lib/Web5Context';
import { EXPANDED_TABS, CHARTS_LIST, NOT_CHART_TYPES } from '../../../constants';
import Charts from '../../Charts';
import Table from '../../Charts/Table';
import { getTableDynamicConfig } from 'lib/services/forceBI';
import { successToast } from 'utils/toast';
import { downloadTable } from '../../../utils/downloadTables';
import { downloadSnapshot } from '../../../utils/downloadSnapshots';
import Empty from '../Empty';

import styles from './styles';

const useStyles = createUseStyles(styles, 'ExpandedWidget');

const Expanded = memo(
    ({
        isOpen,
        data,
        onEdit,
        canEdit,
        config,
        getSnapshot,
        forceBIUtils,
        dashboardFilters,
        googleMapsApiKey,
        onClose,
    }) => {
        const classes = useStyles();
        const { t } = useWeb5Context();
        const [activeTab, setActiveTab] = useState(EXPANDED_TABS.chart);
        const [tableConfig, setTableConfig] = useState(null);
        const [isLoadingTableConfig, setIsLoadingTableConfig] = useState(false);
        const [isErrorTable, setIsErrorTable] = useState(false);
        const imageRef = useRef(null);

        const isChart = useMemo(() => {
            if (CHARTS_LIST.includes(data?.chartType)) return true;
            return false;
        }, [data]);

        // const isMap = useMemo(() => {
        //     if (data?.chartType?.toLowerCase() === NOT_CHART_TYPES.map) return true;
        //     return false;
        // }, [data]);

        const isKpi = useMemo(() => {
            if (data?.chartType?.toLowerCase() === NOT_CHART_TYPES.kpi) return true;
            return false;
        }, [data]);

        const isTable = useMemo(() => {
            if (data?.chartType?.toLowerCase() === NOT_CHART_TYPES.table) return true;
            return false;
        }, [data]);

        const getImage = useCallback(
            (image, isExpanded) => {
                if (!image) return;
                imageRef.current = image;
                getSnapshot({ id: data.id, title: data?.title || '', image });
            },
            [data?.id, data?.title, getSnapshot],
        );

        const updateTable = useCallback(() => {
            if (tableConfig) return;

            const { processDataToSendFromWidget } = forceBIUtils;

            setIsLoadingTableConfig(true);
            setIsErrorTable(false);

            const finalData = processDataToSendFromWidget({
                data,
                dashboardFilters: dashboardFilters,
            });
            getTableDynamicConfig(finalData)
                .then((response) => {
                    if (!response.data) {
                        setIsErrorTable(true);
                        return;
                    }
                    setTableConfig(response.data);
                })
                .catch(() => {
                    setIsErrorTable(true);
                })
                .finally(() => setIsLoadingTableConfig(false));
        }, [tableConfig, data, dashboardFilters, forceBIUtils]);

        const onChangeTab = useCallback(
            (tab) => {
                setActiveTab(tab);
                if (tab === EXPANDED_TABS.table) updateTable();
            },
            [updateTable],
        );

        const onAfterClose = useCallback(() => {
            setActiveTab(EXPANDED_TABS.chart);
            setTableConfig(null);
            imageRef.current = null;
        }, []);

        const onDownloadSnapshot = useCallback(() => {
            if (!imageRef.current) return;
            downloadSnapshot({
                id: data.id,
                title: data?.title || '',
                image: imageRef.current,
                withTitle: true,
            });
            successToast({ t, text: t('label_image_downloaded') });
        }, [data?.id, data?.title, t]);

        const onDownloadTable = useCallback(() => {
            if (!config) return;
            downloadTable({
                title: data.title,
                data: config,
                t,
            });
        }, [t, data?.title, config]);

        const onDownloadChartAsTable = useCallback(() => {
            if (isLoadingTableConfig || !tableConfig) return;
            downloadTable({
                title: tableConfig.title,
                data: tableConfig,
                t,
            });
        }, [isLoadingTableConfig, tableConfig, t]);

        const handlDownloadExpanded = useCallback(() => {
            if (isChart) {
                if (activeTab === EXPANDED_TABS.chart) onDownloadSnapshot();
                else onDownloadChartAsTable();
            } else if (isTable || isKpi) {
                onDownloadTable();
            }
        }, [
            activeTab,
            isChart,
            isTable,
            isKpi,
            onDownloadSnapshot,
            onDownloadChartAsTable,
            onDownloadTable,
        ]);

        const downloadLiteral = useMemo(() => {
            if (activeTab === EXPANDED_TABS.chart) return t('action_download_chart');
            return t('action_download_table');
        }, [activeTab, t]);

        const headerComponent = useMemo(() => {
            if (!canEdit) return null;
            return (
                <div className={classes.headerComponent}>
                    <div className={classes.headerComponentContent}>
                        <Icon name="edit" size="large" onClick={onEdit} tooltip={t('label_edit')} />
                    </div>
                </div>
            );
        }, [classes.headerComponent, classes.headerComponentContent, canEdit, onEdit, t]);

        const isConfirmDisabled = useMemo(() => {
            if (isChart && activeTab === EXPANDED_TABS.table && !tableConfig) return true;
            else return false;
        }, [isChart, activeTab, tableConfig]);

        const modalProps = useMemo(() => {
            return {
                title: data?.title || '',
                size: 'huge',
                isOpen: isOpen,
                onRequestClose: onClose,
                onAfterClose,
                isConfirmDisabled,
                onConfirm: handlDownloadExpanded,
                confirmText: downloadLiteral,
                useContentStaticHeight: true,
                headerComponent,
                overrides: {
                    content: { style: { height: '100%' } },
                    closeIcon: {
                        name: 'fullscreenArrowsExit',
                        tooltip: t('action_close'),
                        size: 'large',
                    },
                },
            };
        }, [
            data?.title,
            isOpen,
            onClose,
            onAfterClose,
            handlDownloadExpanded,
            t,
            downloadLiteral,
            headerComponent,
            isConfirmDisabled,
        ]);

        return (
            <Modal className={classes.root} {...modalProps}>
                <>
                    {isChart && (
                        <>
                            <div className={classes.expandedHeader}>
                                <Tabs
                                    onChange={onChangeTab}
                                    activeKey={activeTab}
                                    tabs={[
                                        { key: EXPANDED_TABS.chart, title: t('label_chart') },
                                        { key: EXPANDED_TABS.table, title: t('label_table') },
                                    ]}
                                />
                            </div>
                            {isOpen && data && (
                                <div className={classes.expandedContent}>
                                    {activeTab === EXPANDED_TABS.chart && (
                                        <Charts
                                            chartType={data?.chartType}
                                            config={config}
                                            getImage={getImage}
                                        />
                                    )}
                                    {activeTab === EXPANDED_TABS.table && (
                                        <>
                                            {isLoadingTableConfig && (
                                                <div className={classes.loader}>
                                                    <Loader size="big" />
                                                </div>
                                            )}
                                            {!isLoadingTableConfig && !isErrorTable && (
                                                <Table data={tableConfig} isExpanded={true} />
                                            )}
                                            {!isLoadingTableConfig && isErrorTable && (
                                                <div className={classes.empty}>
                                                    <Empty type="error" onRefresh={updateTable} />
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                    {!isChart && data && (
                        <Charts
                            chartType={data?.chartType}
                            config={config}
                            getImage={getImage}
                            googleMapsApiKey={googleMapsApiKey}
                            isExpanded={true}
                        />
                    )}
                </>
            </Modal>
        );
    },
);

export default Expanded;
